import type { ReactElement } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'

import { Select } from '../Select/Select'
import styles from './sideNav.module.scss'

export type SideNavLink = {
  to: string
  label: string
}

export type SideNavGroup = {
  title: string
  links: Array<SideNavLink>
}

export type SideNavProps = {
  /** The accessible name of the navigation landmark ("Administración"). */
  label: string
  groups: Array<SideNavGroup>
}

/** The link the current URL belongs to: the longest `to` the path starts with. */
const currentLink = (pathname: string, links: Array<SideNavLink>): SideNavLink | undefined =>
  links
    .filter((link) => pathname === link.to || pathname.startsWith(`${link.to}/`))
    .sort((left, right) => right.to.length - left.to.length)[0]

/**
 * A sidebar of grouped links for an area with more screens than the top bar can
 * hold. Below the phone breakpoint the column would eat the screen, so it
 * becomes one `<select>` at the top ("Ir a") instead — a native control, which
 * on a phone opens the system picker, bigger than any drawer we would draw.
 *
 * Both are rendered and CSS shows one: `display: none` also takes the hidden
 * one out of the accessibility tree, so nobody hears the menu twice.
 */
export const SideNav = ({ label, groups }: SideNavProps): ReactElement => {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const links = groups.flatMap((group) => group.links)
  const current = currentLink(pathname, links)

  const linkClass = ({ isActive }: { isActive: boolean }): string =>
    [styles['side-nav__link'], isActive ? styles['side-nav__link--active'] : ''].filter(Boolean).join(' ')

  return (
    <div className={styles['side-nav']}>
      <nav className={styles['side-nav__column']} aria-label={label}>
        {groups.map((group) => (
          <div key={group.title} className={styles['side-nav__group']}>
            <p className={styles['side-nav__group-title']}>{group.title}</p>
            <ul className={styles['side-nav__list']}>
              {group.links.map((link) => (
                <li key={link.to}>
                  <NavLink to={link.to} className={linkClass}>{link.label}</NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      <div className={styles['side-nav__compact']}>
        <Select
          label="Ir a"
          value={current?.to ?? ''}
          placeholder={current === undefined ? 'Elige una pantalla…' : undefined}
          options={groups.flatMap((group) =>
            group.links.map((link) => ({ value: link.to, label: `${link.label} (${group.title})` })))}
          onChange={(event) => {
            if (event.target.value !== '') {
              navigate(event.target.value)
            }
          }}
        />
      </div>
    </div>
  )
}
